<template>
  <div class="uk-container full-page">
    <UserHeader />
    <div class="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom">
      <h1>Ingredient Checker History</h1>
      <router-link v-if="showNewScanAction" to="/ingredient-checker" class="uk-button uk-button-primary uk-button-small">
        <span uk-icon="icon: plus" class="uk-margin-small-right"></span>New Scan
      </router-link>
    </div>

    <p v-if="isLoading">Loading...</p>
    <p v-else-if="error" class="uk-text-danger">{{ error }}</p>
    <p v-else-if="!history.length">No label scans yet. Start by scanning a product label.</p>

    <div v-else>
      <div
        v-for="item in history"
        :key="item.id"
        class="uk-card uk-card-default uk-card-body history-card"
      >
        <!-- Kit info row (like TestKits) -->
        <div class="history-kit-header uk-margin-small-bottom">
          <div class="ucari-card-title">
            <span v-if="item.type_description === 'Pets'">Pet </span>Intolerance Kit - {{ item.type_name || 'N/A' }}
          </div>
          <div class="uk-child-width-expand@s uk-grid uk-grid-small uk-margin-small-top">
            <div class="columns">
              <label>Name</label>
              <div>{{ item.first_name || item.pet_name || '' }} {{ item.last_name || '' }}</div>
            </div>
            <div class="columns">
              <label>Kit ID</label>
              <div>{{ item.serial_number || 'N/A' }}</div>
            </div>
            <div class="columns">
              <label>Scanned On</label>
              <div>{{ formatDate(item.created_at) }}</div>
            </div>
          </div>
        </div>

        <!-- Product name identified from the uploaded label -->
        <div v-if="item.product_name" class="uk-margin-small-bottom">
          <strong>Product:</strong> {{ item.product_name }}
        </div>

        <!-- Image thumbnails -->
        <div class="history-images uk-margin-small-bottom">
          <div class="history-img-wrap">
            <div class="uk-text-small uk-text-muted">Brand / Product</div>
            <img
              v-if="item.brand_image_url"
              :src="item.brand_image_url"
              alt="brand"
              class="history-thumb"
              @click="openImage(item.brand_image_url)"
            />
            <div v-else class="history-thumb-placeholder">No image</div>
          </div>
          <div class="history-img-wrap">
            <div class="uk-text-small uk-text-muted">Ingredients Label</div>
            <img
              v-if="item.ingredient_image_url"
              :src="item.ingredient_image_url"
              alt="ingredients"
              class="history-thumb"
              @click="openImage(item.ingredient_image_url)"
            />
            <div v-else class="history-thumb-placeholder">No image</div>
          </div>
        </div>

        <!-- Collapsed results (toggle) -->
        <button class="uk-button uk-button-secondary" @click="item._expanded = !item._expanded">
          {{ item._expanded ? 'Hide Details ▲' : 'Show Details ▼' }}
        </button>

        <div v-if="item._expanded" class="uk-margin-small-top">
          <IngredientCheckNotice :result="item" />
          <!-- Ingredients -->
          <div v-if="item.ingredients_raw" class="result-card">
            <div class="result-card__header">
              <span class="result-card__icon" uk-icon="icon: list; ratio: 1.1"></span>
              <h3 class="result-card__title">{{ isHistorical(item) ? 'Previously Read Ingredient Text' : 'Text Read From Your Ingredient List' }}</h3>
            </div>
            <pre class="ingredients-pre">{{ item.ingredients_raw }}</pre>
          </div>

          <!-- Matches -->
          <div v-if="sortedMatches(item).length" class="result-card">
            <div class="result-card__header">
              <span class="result-card__icon" uk-icon="icon: warning; ratio: 1.1"></span>
              <h3 class="result-card__title">{{ isHistorical(item) ? 'Previously Reported Matches' : "Matches Your Pet's Intolerances" }}</h3>
            </div>
            <div class="match-list uk-margin-small-top">
              <div v-for="(m, idx) in sortedMatches(item)" :key="'m' + idx" class="match-item">
                <span class="match-item__intolerance">{{ m.test_item_name }}</span>
                <span class="match-item__arrow">→</span>
                <span class="match-item__ingredient">{{ m.matched_ingredient }}</span>
                <span class="match-item__severity" :class="'severity--' + normalizedSeverity(m)">
                  {{ severityLabel(m) }}
                </span>
              </div>
            </div>
          </div>
          <div v-else-if="isHistorical(item) && item.comparison.historical_status === 'complete'" class="result-card">
            <div class="result-card__header">
              <span class="result-card__icon" uk-icon="icon: history; ratio: 1.1"></span>
              <h3 class="result-card__title">Saved Result: No Matches Reported</h3>
            </div>
            <p class="result-card__subtitle">The earlier check reported no matches. This historical result has not been reverified.</p>
          </div>
          <div v-else-if="canShowNoMatches(item)" class="result-card result-card--success">
            <div class="result-card__header">
              <span class="result-card__icon" uk-icon="icon: check; ratio: 1.1"></span>
              <h3 class="result-card__title">No Intolerance Matches Found</h3>
            </div>
            <p class="result-card__subtitle">
              We didn't find any ingredients in this product that clearly match your intolerance report.
            </p>
          </div>

          <!-- Not matched -->
          <div v-if="!isHistorical(item) && item.comparison?.analysis_status === 'complete' && item.comparison?.not_matched?.length" class="result-card result-card--info">
            <div class="result-card__header">
              <span class="result-card__icon" uk-icon="icon: info; ratio: 1.1"></span>
              <h3 class="result-card__title">Intolerance Items Not Found in This Product</h3>
            </div>
            <div class="not-matched-chips">
              <span v-for="(nm, idx) in item.comparison.not_matched" :key="'nm' + idx" class="chip chip--safe">
                {{ nm.test_item_name }}
              </span>
            </div>
          </div>

          <!-- What This Means -->
          <div v-if="item.comparison?.notes && !isHistorical(item)" class="result-card">
            <div class="result-card__header">
              <span class="result-card__icon" uk-icon="icon: comment; ratio: 1.1"></span>
              <h3 class="result-card__title">What This Means</h3>
            </div>
            <p class="result-card__notes">{{ item.comparison.notes }}</p>
          </div>

          <!-- Disclaimer -->
          <div class="disclaimer-box">
            <p class="disclaimer-text">
              <strong>Disclaimer:</strong> This tool automatically reads product labels and compares ingredients against your pet's intolerance test results. Results are for informational purposes only and should not be considered veterinary or medical advice. Ingredient detection may not be 100% accurate due to image quality, label formatting, or automated interpretation. The results reflect potential intolerances identified through testing and do not constitute an evaluation or endorsement of any product or brand. Always consult your veterinarian before making dietary changes for your pet.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Image lightbox -->
    <div v-if="lightboxUrl" class="lightbox-overlay" @click="lightboxUrl = ''">
      <img :src="lightboxUrl" alt="full size" class="lightbox-img" />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import UserHeader from "./UserHeader.vue";
import IngredientCheckNotice from './IngredientCheckNotice.vue';
import { canShowNoMatches } from '../utils/ingredientCheckResult';
import { convertUtcToLocal, shouldHideIngredientChecker } from "@/utils/common";

export default {
  name: "LabelReaderHistory",
  components: { UserHeader, IngredientCheckNotice },

  data() {
    return {
      history: [],
      isLoading: false,
      error: "",
      lightboxUrl: "",
      accessToken: "",
      cid: "",
    };
  },

  created() {
    document.title = "Ingredient Checker History | UCARI";
    this.accessToken = localStorage.getItem("accessToken");
    this.cid = localStorage.getItem("cid");
    this.fetchHistory();
  },

  computed: {
    showNewScanAction() {
      return !shouldHideIngredientChecker(this.cid);
    },
  },

  methods: {
    canShowNoMatches,
    isHistorical(item) { return item.comparison?.issue_code === 'legacy_unverified'; },
    async fetchHistory() {
      this.isLoading = true;
      this.error = "";
      try {
        const resp = await axios.get(
          `${process.env.VUE_APP_API_URL}/api/label-reader/history`,
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`,
              cid: this.cid,
            },
          }
        );
        // Add _expanded flag for toggle UI
        this.history = (resp.data || []).map((item) => ({
          ...item,
          _expanded: false,
        }));
      } catch (err) {
        if (err.response?.status === 401) {
          this.$router.push("/login");
          return;
        }
        this.error =
          err.response?.data?.message ||
          err.message ||
          "Failed to load history.";
      } finally {
        this.isLoading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return convertUtcToLocal(dateStr);
    },

    openImage(url) {
      this.lightboxUrl = url;
    },

    sortedMatches(item) {
      const matches = item.comparison?.matched || [];
      const severityOrder = { severe: 0, moderate: 1 };
      const confidenceOrder = { high: 0, medium: 1, low: 2 };
      return [...matches].sort((a, b) => {
        const severityDiff = (severityOrder[this.normalizedSeverity(a)] ?? 3) - (severityOrder[this.normalizedSeverity(b)] ?? 3);
        if (severityDiff !== 0) return severityDiff;
        return (confidenceOrder[a.confidence] ?? 3) - (confidenceOrder[b.confidence] ?? 3);
      });
    },

    normalizedSeverity(match) {
      const value = String(match?.severity || match?.severity_label || "").toLowerCase();
      if (value === "severe" || value === "high") return "severe";
      if (value === "moderate" || value === "mild" || value === "medium") return "moderate";
      return "unknown";
    },

    severityLabel(match) {
      const severity = this.normalizedSeverity(match);
      if (severity === "severe") return "Severe";
      if (severity === "moderate") return "Moderate";
      return "Matched";
    },
  },
};
</script>
